import { Inject } from '@odinjs/odin';

import { ChecklistAPI, FormAPI, ModalButton, ModalProps, ModalService } from '@philips-internal/oswald-ui-components';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { ModalButtonType } from '@philips-internal/oswald-ui-constants';
import { AbortableCloseEvent, FetchEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import Controller, { OnAfterFetch, OnCancel, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { Value } from '@philips-internal/oswald-ui-core';

import BaseController from '../../../../../controllers/BaseController';
import { FragmentNames, SelectOngoingNursingInterventionsAliases } from '../../../../../codegen';
import { FoundationUtils } from '../../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../../../utils/parameters';
import { BooleanUtils } from '../../../../../utils/boolean-utils';
import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';
import ExecuteNursingInterventionsService from '../nursing-interventions-start/services/start-nursing-interventions-service';
import { YesNoType } from '../../../../../utils/enum-utils';
import { ApplicationRoute } from '../../../../../constants/routes';
import { AdminModalsService } from '../../../services/admin-modals-service';
import { NursingInterventionSchedule } from './nursing-interventions-records-type';
import { SavedRecordsContext } from './services/saved-records-context';

@Controller({ component: SelectOngoingNursingInterventionsAliases.LAYOUT_SELECT_ONGOING_NURSING_INTERVENTIONS, fragment: FragmentNames.SELECT_ONGOING_NURSING_INTERVENTIONS })
export default class SelectOngoingNursingInterventionsController extends BaseController {

  @Inject(SelectOngoingNursingInterventionsAliases.FORM_SELECT_ONGOING_NURSING_INTERVENTIONS)
  private formApi!: FormAPI

  @Inject(SelectOngoingNursingInterventionsAliases.CHECKLIST_SELECT_ONGOING_NURSING_INTERVENTIONS)
  private checklistApi!: ChecklistAPI

  @OnMount(SelectOngoingNursingInterventionsAliases.LAYOUT_SELECT_ONGOING_NURSING_INTERVENTIONS)
  handleMountLayout(): void {
    const { prescriptionNumber, startDate } = this.historyService.getCurrentRouteParams();
    this.context.set({
      prescriptionId: prescriptionNumber,
      scheduleDate: startDate
    })
  }

  @OnAfterFetch(SelectOngoingNursingInterventionsAliases.FETCH_SELECT_ONGOING_NURSING_INTERVENTIONS)
  handleAfterFetchOngoingNursingInterventions(event: FetchEvent): void {
    const ongoingInterventions = event.data as NursingInterventionSchedule[]
    if (ongoingInterventions.length === 0 || this.mustAdministerAllSAE()) {
      this.checklistApi.checkAll()
    }
    this.context.set({ ongoingInterventions })
  }

  @OnCancel(SelectOngoingNursingInterventionsAliases.FORM_SELECT_ONGOING_NURSING_INTERVENTIONS)
  handleCancel(): void {
    ModalService.close(FragmentNames.SELECT_ONGOING_NURSING_INTERVENTIONS);
  }

  @OnSubmit(SelectOngoingNursingInterventionsAliases.FORM_SELECT_ONGOING_NURSING_INTERVENTIONS)
  handleSubmitForm(): void {
    const interventionsSelected = this.checklistApi.getValue() as unknown as NursingInterventionSchedule[];

    if (!interventionsSelected || interventionsSelected?.length === 0) {
      ModalService.close(FragmentNames.SELECT_ONGOING_NURSING_INTERVENTIONS);
      return
    }
    const allInterventionsSelected = this.context.get('ongoingInterventions') as NursingInterventionSchedule[];
    const interventions = interventionsSelected.map((intervention) => allInterventionsSelected.find((i) => i.id === intervention.key));
    const recordsVitalSigns = interventions.filter((i) => i?.vitalSignsQt ?? 0 > 0);
    const nonRecordVitalSigns = interventions.filter((i) => i?.vitalSignsQt ?? 0 === 0);
    if (this.isRequiredEmarRecords() && recordsVitalSigns.length > 0) {
      this.openEmarRecordsForms(recordsVitalSigns);
      if (nonRecordVitalSigns.length === 0) {
        return;
      }
    }

    Promise.all([interventions.map((intervention) => {
      return this.executeNursingIntevention(intervention)
    })])
      .then(() => {
        this.redirectToTimeline()
      })
      .catch(() => {
        this.formApi.setLoading(false)
        this.formApi.setDisabled(false)
      })
      .finally(() => {
        ModalService.close(FragmentNames.SELECT_ONGOING_NURSING_INTERVENTIONS);
      })
  }

  private executeNursingIntevention(intervention?: NursingInterventionSchedule): Promise<void> {
    const requestBody: Record<string, Value> = {
      alterationDate: this.context.get('alterationDate'),
      encounterNumber: MedicineAndSolutionsParams.params.encounterId,
      eventDate: MedicineAndSolutionsParams.params.startId,
      hourSequence: MedicineAndSolutionsParams.params.hourSequence,
      intervalCode: MedicineAndSolutionsParams.params.cdIntervalId,
      itemCode: intervention?.itemCode,
      itemSequence: intervention?.id,
      itemType: MedicineAndSolutionsParams.params.ieTypeItem,
      justifyDescription: this.context.get('justifyDescription') || '',
      observationDescription: this.context.get('observationDescription') || '',
      prescriptionNumber: intervention?.prescriptionNumber,
      scheduleSequence: intervention?.scheduleSequence,
    } as Record<string, Value>;

    return ExecuteNursingInterventionsService
      .executeWithoutMessages(requestBody)
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    AdminModalsService.redirectToTimeline(FragmentNames.NURSING_INTERVENTIONS_START);
    AdminModalsService.redirectToTimeline(FragmentNames.NURSING_INTERVENTIONS_VIEWER);
    AdminModalsService.redirectToTimeline(FragmentNames.SELECT_ONGOING_NURSING_INTERVENTIONS);
  }

  private openEmarRecordsForms(allInterventions: (NursingInterventionSchedule | undefined)[]): void {
    const currentIntervention = allInterventions?.shift()
    this.context.set({ allInterventions, currentIntervention })
    const title = MessageService.translate('text.sncp-care-plan');
    const nursingInterventions = this.context.getBulkData();
    const startNursingInterventionsFragment = FragmentNames.NURSING_INTERVENTIONS_EMAR_RECORDS
    const id = `${startNursingInterventionsFragment}-${currentIntervention?.id}`
    const modalProperties: ModalProps = {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: startNursingInterventionsFragment,
      id,
      onBeforeClose: (event) => {
        this.openDialogModalConfirm(event, id)
      },
      onClose: () => {
        if (allInterventions.length) {
          this.openEmarRecordsForms(this.context.get('allInterventions'))
          return
        }
        this.redirectToTimeline()
        SavedRecordsContext.clean()
      },
      title,
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      currentIntervention,
      nursingInterventions
    }

    ModalService.openDefault(modalProperties);
  }

  private openDialogModalConfirm(event: AbortableCloseEvent, fragment: string): void {
    event.abort();

    const buttons: ModalButton[] = [];

    buttons.push({
      action: () => { },
      title: 'tws.biometry.cancel',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => {
        ModalService.close(FragmentNames.SELECT_ONGOING_NURSING_INTERVENTIONS)
        ModalService.close(fragment)
      },
      title: 'tws.file-uploader-modal.confirm',
      type: ModalButtonType.SUCCESS,
    });

    const dialogModal: ModalProps = {
      closeByDocument: false,
      closeByEscape: false,
      dialogDescription: MessageService.translate('text.reset items-want-to-continue'),
      dockButtons: buttons,
      focusOnOpen: true,
      title: 'text.message-alert-attention',
      uuid: generateUUID(),
    };

    ModalService.openDialog(dialogModal);
  }

  private isRequiredEmarRecords(): boolean {
    const isRequiredEmarRecords = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.RECORD_VITAL_SIGNS_WHEN_ADMINISTERING_AN_SNCP_INTERVENTION);
    return BooleanUtils.isTrueValue(String(isRequiredEmarRecords))
  }

  private mustAdministerAllSAE(): boolean {
    const mustAdministerAllSAE = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.WHEN_ADMINISTERING_THE_SNCP_ASK_WHETHER_TO_ADMINISTER_ALL_AT_THE_SAME_TIME_SLOT);
    return mustAdministerAllSAE === YesNoType.YES
  }
}